import { ScrollView } from "react-native";
import type { Href } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { TenantLink } from "@src/features/tenants";
import { activationCameraKey, type CursorPager, type FleetSignalSummary } from "@visioni/a3s-monitoring/app";
import { EMPTY, formatPreciseInstant, formatText } from "../format";
import { CursorPagerBar, countOf } from "./CursorPagerBar";

/** A row of either list: the organisation's adds where on its site the signal came from. */
type SignalRowData = FleetSignalSummary & { readonly a3sSite?: string; readonly a3sCamera?: string };

interface SignalsTableProps<T extends SignalRowData> {
  readonly items: readonly T[];
  /** Dim the rows while they are the previous page's, held until the next arrives. */
  readonly isStale: boolean;
  readonly hrefFor: (signal: T) => Href;
  /** Adds Site and Camera columns, for a list within one organisation. */
  readonly placement?: {
    readonly siteLabel: (siteId: string) => string;
    readonly cameraLabel: (cameraKey: string) => string;
  };
  /** The zone the Received column is shown in — the one the list is filtered in. */
  readonly timeZone: string;
  readonly pager: CursorPager;
  readonly nextCursor: string | null;
  readonly isFetching: boolean;
}

const COLUMNS = [
  { label: "Received", style: "received" },
  { label: "Client", style: "client" },
  { label: "Type", style: "type" },
  { label: "Description", style: "description" },
  { label: "Site", style: "site" },
  { label: "Camera", style: "camera" },
  { label: "Activation", style: "ticket" },
] as const;

/**
 * Signals, one per row, each opening on its own page. Laid out like
 * `ActivationsTable`: the whole row is the link, so client and activation are
 * plain text here and linked from the signal's page.
 */
export function SignalsTable<T extends SignalRowData>({
  items,
  isStale,
  hrefFor,
  placement,
  timeZone,
  pager,
  nextCursor,
  isFetching,
}: SignalsTableProps<T>) {
  const columns = COLUMNS.filter((c) => (c.label !== "Site" && c.label !== "Camera") || placement !== undefined);

  return (
    <View flex={1}>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.card, isStale && styles.stale]}>
          <View style={[styles.row, styles.headerRow]}>
            {columns.map((column) => (
              <Text key={column.label} textStyle="xs/semibold" color="textMuted" style={styles[column.style]}>
                {column.label}
              </Text>
            ))}
          </View>
          {items.map((signal) => (
            <TenantLink key={signal.signalId} href={hrefFor(signal)} style={styles.link}>
              {({ hovered }) => (
                <SignalCells signal={signal} hovered={hovered} placement={placement} timeZone={timeZone} />
              )}
            </TenantLink>
          ))}
          {items.length === 0 && (
            <View style={styles.row}>
              <Text textStyle="sm/regular" color="textMuted">
                {nextCursor === null ? "No more signals" : "None on this page — more may follow"}
              </Text>
            </View>
          )}
        </View>
      </ScrollView>
      <CursorPagerBar
        pager={pager}
        nextCursor={nextCursor}
        isFetching={isFetching}
        summary={countOf(items.length, "signal")}
      />
    </View>
  );
}

interface SignalCellsProps {
  readonly signal: SignalRowData;
  readonly hovered: boolean;
  readonly placement: SignalsTableProps<SignalRowData>["placement"];
  readonly timeZone: string;
}

function SignalCells({ signal, hovered, placement, timeZone }: SignalCellsProps) {
  const cameraKey = activationCameraKey(signal);

  return (
    <View style={[styles.row, hovered && styles.hovered]}>
      <Text textStyle="xs/regular" color="text" style={styles.received}>
        {formatPreciseInstant(signal.receivedDateTime, timeZone)}
      </Text>
      <Text textStyle="xs/regular" color="text" style={styles.client} numberOfLines={1}>
        {signal.clientNo ?? EMPTY}
      </Text>
      <Text textStyle="xs/regular" color="text" style={styles.type}>
        {signal.typeNo !== undefined ? String(signal.typeNo) : EMPTY}
      </Text>
      <Text textStyle="sm/regular" color="text" style={styles.description} numberOfLines={2}>
        {formatText(signal.description)}
      </Text>
      {placement !== undefined && (
        <>
          <Text textStyle="xs/regular" color="text" style={styles.site} numberOfLines={1}>
            {signal.a3sSite !== undefined ? placement.siteLabel(signal.a3sSite) : EMPTY}
          </Text>
          <Text textStyle="xs/regular" color="text" style={styles.camera} numberOfLines={1}>
            {cameraKey !== undefined ? placement.cameraLabel(cameraKey) : (signal.a3sCamera ?? EMPTY)}
          </Text>
        </>
      )}
      {/* No ticket is normal for a test signal, which never raises one. */}
      <Text textStyle="xs/regular" color="text" style={styles.ticket}>
        {signal.ticketNo ?? EMPTY}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  scroll: { flex: 1 },
  scrollContent: { padding: theme.spacing.xl },
  card: {
    backgroundColor: theme.colors.surfaceCard,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
    overflow: "hidden",
  },
  stale: { opacity: 0.5 },
  link: { cursor: "pointer" },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.lg,
    borderTopWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  headerRow: { borderTopWidth: 0, backgroundColor: theme.colors.surfaceCardInner },
  hovered: { backgroundColor: theme.colors.surfaceCardInner },
  received: { width: 200 },
  client: { width: 130 },
  type: { width: 60 },
  description: { flex: 2, minWidth: 140 },
  site: { flex: 1, minWidth: 100 },
  camera: { flex: 1, minWidth: 100 },
  ticket: { width: 100 },
}));
